// Notification Sound Utility for SMBC App
// Comprehensive sound system for all app events

class NotificationSoundManager {
  constructor() {
    this.audioContext = null;
    this.isEnabled = true;
    this.lastPlayTime = 0;
    this.minInterval = 1500; // Minimum 1.5 seconds between sounds
    
    // Load user preference from localStorage
    const savedPref = localStorage.getItem('notificationSoundEnabled');
    this.isEnabled = savedPref === null ? true : savedPref === 'true';
  }

  // Initialize AudioContext (must be called after user interaction)
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this;
  }

  // Enable/disable notification sounds
  setEnabled(enabled) {
    this.isEnabled = enabled;
    localStorage.setItem('notificationSoundEnabled', enabled.toString());
  }

  // Check if sounds are enabled
  getEnabled() {
    return this.isEnabled;
  }

  // Check if can play (rate limiting)
  canPlay() {
    if (!this.isEnabled) return false;
    const now = Date.now();
    if (now - this.lastPlayTime < this.minInterval) return false;
    this.lastPlayTime = now;
    return true;
  }

  // Initialize audio context if needed
  prepareContext() {
    this.init();
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    return this.audioContext.currentTime;
  }

  // ============== SOUND TYPES ==============

  // 1. General Notification - Bright ascending chime (for alerts, updates)
  playNotificationSound() {
    if (!this.canPlay()) return;
    try {
      const now = this.prepareContext();
      
      // Three-note ascending chime (C5 -> E5 -> G5)
      const notes = [523, 659, 784]; // C5, E5, G5
      notes.forEach((freq, i) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.frequency.setValueAtTime(freq, now + i * 0.1);
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.25, now + i * 0.1);
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.25);
        osc.start(now + i * 0.1);
        osc.stop(now + i * 0.1 + 0.25);
      });
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  }

  // 2. New Message - Soft double pop (for DMs, inbox messages)
  playMessageSound() {
    if (!this.canPlay()) return;
    try {
      const now = this.prepareContext();
      
      // Double pop sound
      [0, 0.12].forEach((delay) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.frequency.setValueAtTime(880, now + delay); // A5
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.2, now + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, now + delay + 0.1);
        osc.start(now + delay);
        osc.stop(now + delay + 0.1);
      });
    } catch (error) {
      console.log('Could not play message sound:', error);
    }
  }

  // 3. Group Chat Message - Bubbly pop (for group chat activity)
  playGroupChatSound() {
    if (!this.canPlay()) return;
    try {
      const now = this.prepareContext();
      
      // Bubbly rising pop
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      osc.connect(gain);
      gain.connect(this.audioContext.destination);
      osc.frequency.setValueAtTime(600, now);
      osc.frequency.exponentialRampToValueAtTime(900, now + 0.15);
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
      osc.start(now);
      osc.stop(now + 0.2);
    } catch (error) {
      console.log('Could not play group chat sound:', error);
    }
  }

  // 4. Prayer Request - Gentle bell tone (for prayer community)
  playPrayerSound() {
    if (!this.canPlay()) return;
    try {
      const now = this.prepareContext();
      
      // Soft bell-like tone with harmonics
      const fundamental = 440; // A4
      [1, 2, 3].forEach((harmonic, i) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.frequency.setValueAtTime(fundamental * harmonic, now);
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.15 / harmonic, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
        osc.start(now);
        osc.stop(now + 0.8);
      });
    } catch (error) {
      console.log('Could not play prayer sound:', error);
    }
  }

  // 5. Benevolent Update - Warm descending tone (for benevolent requests)
  playBenevolentSound() {
    if (!this.canPlay()) return;
    try {
      const now = this.prepareContext();
      
      // Warm descending two-note (G5 -> E5)
      const notes = [784, 659];
      notes.forEach((freq, i) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.frequency.setValueAtTime(freq, now + i * 0.15);
        osc.type = 'triangle'; // Warmer tone
        gain.gain.setValueAtTime(0.2, now + i * 0.15);
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.15 + 0.3);
        osc.start(now + i * 0.15);
        osc.stop(now + i * 0.15 + 0.3);
      });
    } catch (error) {
      console.log('Could not play benevolent sound:', error);
    }
  }

  // 6. Group Join Request - Attention-getting tone (for group leaders)
  playJoinRequestSound() {
    if (!this.canPlay()) return;
    try {
      const now = this.prepareContext();
      
      // Three quick beeps
      [0, 0.15, 0.3].forEach((delay) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.frequency.setValueAtTime(1047, now + delay); // C6
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.15, now + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, now + delay + 0.08);
        osc.start(now + delay);
        osc.stop(now + delay + 0.08);
      });
    } catch (error) {
      console.log('Could not play join request sound:', error);
    }
  }

  // 7. Announcement - Fanfare-like tone (for new announcements)
  playAnnouncementSound() {
    if (!this.canPlay()) return;
    try {
      const now = this.prepareContext();
      
      // Fanfare chord (C major)
      const notes = [523, 659, 784]; // C5, E5, G5
      notes.forEach((freq) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.frequency.setValueAtTime(freq, now);
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.12, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
        osc.start(now);
        osc.stop(now + 0.5);
      });
    } catch (error) {
      console.log('Could not play announcement sound:', error);
    }
  }

  // 8. Success Sound - Positive confirmation (for completed actions)
  playSuccessSound() {
    if (!this.canPlay()) return;
    try {
      const now = this.prepareContext();
      
      // Quick ascending two-note (E5 -> G5)
      const notes = [659, 784];
      notes.forEach((freq, i) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.frequency.setValueAtTime(freq, now + i * 0.08);
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.18, now + i * 0.08);
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.15);
        osc.start(now + i * 0.08);
        osc.stop(now + i * 0.08 + 0.15);
      });
    } catch (error) {
      console.log('Could not play success sound:', error);
    }
  }

  // 9. Alert/Warning Sound - Attention tone (for important alerts)
  playAlertSound() {
    if (!this.canPlay()) return;
    try {
      const now = this.prepareContext();
      
      // Two-tone alert (descending)
      const notes = [880, 660]; // A5, E5
      notes.forEach((freq, i) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.frequency.setValueAtTime(freq, now + i * 0.2);
        osc.type = 'square';
        gain.gain.setValueAtTime(0.1, now + i * 0.2);
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.2 + 0.15);
        osc.start(now + i * 0.2);
        osc.stop(now + i * 0.2 + 0.15);
      });
    } catch (error) {
      console.log('Could not play alert sound:', error);
    }
  }

  // 10. Gentle Reminder - Soft single tone (for subtle notifications)
  playReminderSound() {
    if (!this.canPlay()) return;
    try {
      const now = this.prepareContext();
      
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      osc.connect(gain);
      gain.connect(this.audioContext.destination);
      osc.frequency.setValueAtTime(587, now); // D5
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
      osc.start(now);
      osc.stop(now + 0.4);
    } catch (error) {
      console.log('Could not play reminder sound:', error);
    }
  }
}

// Create singleton instance
const notificationSound = new NotificationSoundManager();

export default notificationSound;
